import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  FaCheck,
  FaClipboardCheck,
  FaDatabase,
  FaPalette,
  FaSearch,
  FaShieldAlt,
  FaWordpress,
} from 'react-icons/fa';
import type { IconBaseProps, IconType } from 'react-icons';
import type { ServicePriceKey } from '../../../config/pricing';
import {
  getServicePriceSnapshot,
  getSiteCurrency,
} from '../../../utils/servicePricing';
import { trackMatomoEvent } from '../../../utils/matomo';
import './styles.css';

interface TeaserCard {
  key: ServicePriceKey;
  title: string;
  desc: string;
  features: string[];
  Icon: React.ComponentType<IconBaseProps>;
  recommendedLabel?: string;
  pricing: ReturnType<typeof getServicePriceSnapshot>;
}

const asIconComponent = (Icon: IconType, name: string) => {
  const Comp: React.FC<IconBaseProps> = (props) => {
    const element = Icon(props);
    return React.isValidElement(element) ? element : null;
  };
  Comp.displayName = name;
  return Comp;
};

const CheckIcon = asIconComponent(FaCheck, 'CheckIcon');
const SearchIcon = asIconComponent(FaSearch, 'SearchIcon');
const DatabaseIcon = asIconComponent(FaDatabase, 'DatabaseIcon');
const PaletteIcon = asIconComponent(FaPalette, 'PaletteIcon');
const WordpressIcon = asIconComponent(FaWordpress, 'WordpressIcon');
const ShieldIcon = asIconComponent(FaShieldAlt, 'ShieldIcon');
const ClipboardCheckIcon = asIconComponent(
  FaClipboardCheck,
  'ClipboardCheckIcon',
);

const ServicesTeaser = () => {
  const { i18n, t } = useTranslation();
  const currency = getSiteCurrency(process.env);
  const locale = i18n.resolvedLanguage ?? i18n.language;

  const cards: TeaserCard[] = [
    {
      key: 'tech-seo',
      title: t('services.plan_tech_seo_title'),
      desc: t('services.plan_tech_seo_desc'),
      Icon: SearchIcon,
      recommendedLabel: t('services.plan_recommended_fast_impact'),
      features: [
        t('services.features.deliverable_seo_audit'),
        t('services.features.deliverable_cw_vitals'),
        t('services.features.deliverable_accessibility'),
      ],
      pricing: getServicePriceSnapshot('tech-seo', {
        currency,
        locale,
      }),
    },
    {
      key: 'pim',
      title: t('services.plan_pim_title'),
      desc: t('services.plan_pim_desc'),
      Icon: DatabaseIcon,
      features: [
        t('services.features.pim_integration'),
        t('services.features.pim_data_model'),
        t('services.features.pim_quality_rules'),
      ],
      pricing: getServicePriceSnapshot('pim', {
        currency,
        locale,
      }),
    },
    {
      key: 'wordpress',
      title: t('services.plan_wordpress_title'),
      desc: t('services.plan_wordpress_desc'),
      Icon: WordpressIcon,
      features: [
        t('services.features.wp_build_or_rebuild'),
        t('services.features.wp_performance'),
        t('services.features.wp_security_hardening'),
      ],
      pricing: getServicePriceSnapshot('wordpress', {
        currency,
        locale,
      }),
    },
    {
      key: 'ux',
      title: t('services.plan_ux_title'),
      desc: t('services.plan_ux_desc'),
      Icon: PaletteIcon,
      features: [
        t('services.features.ux_layout_refresh'),
        t('services.features.ux_user_flows'),
      ],
      pricing: getServicePriceSnapshot('ux', {
        currency,
        locale,
      }),
    },
    {
      key: 'cyber',
      title: t('services.plan_cyber_title'),
      desc: t('services.plan_cyber_desc'),
      Icon: ShieldIcon,
      features: [
        t('services.features.cyber_risk_assessment'),
        t('services.features.cyber_hardening'),
        t('services.features.cyber_monitoring'),
      ],
      pricing: getServicePriceSnapshot('cyber', {
        currency,
        locale,
      }),
    },
    {
      key: 'audit',
      title: t('services.plan_audit_title'),
      desc: t('services.plan_audit_desc'),
      Icon: ClipboardCheckIcon,
      features: [
        t('services.plan_audit_feature_privacy'),
        t('services.plan_audit_feature_performance'),
        t('services.plan_audit_feature_accessibility'),
      ],
      pricing: getServicePriceSnapshot('audit', {
        currency,
        locale,
      }),
    },
  ];

  return (
    <section
      className="services-teaser"
      aria-labelledby="services-teaser-title"
    >
      <header className="teaser-header">
        <h2 id="services-teaser-title">{t('home.services_teaser_title')}</h2>
        <p className="teaser-subtitle">{t('home.services_teaser_subtitle')}</p>
        <p className="teaser-price-note">
          {t('home.services_teaser_price_note')}
        </p>
      </header>

      <div className="teaser-grid">
        {cards.map((card) => (
          <article className="teaser-card" key={card.key}>
            <div className={`teaser-cover teaser-cover--${card.key}`} />
            {card.recommendedLabel && (
              <span className="teaser-recommended">
                {card.recommendedLabel}
              </span>
            )}
            <div className="teaser-card-body">
              <div className="teaser-card-header">
                <span className="teaser-badge" aria-hidden>
                  <card.Icon className="teaser-icon" />
                </span>
                <h3 className="teaser-title">{card.title}</h3>
              </div>
              {card.pricing.hasConfiguredPrice && (
                <div className="teaser-price">
                  <span className="teaser-price-label">
                    {t('services.plan_price_from')}
                  </span>
                  <strong className="teaser-price-value">
                    {card.pricing.formattedPrice}
                  </strong>
                </div>
              )}
              <p className="teaser-desc">{card.desc}</p>
              <ul className="teaser-features">
                {card.features.map((feature) => (
                  <li key={feature}>
                    <span className="teaser-feature-icon" aria-hidden>
                      <CheckIcon />
                    </span>
                    <span className="teaser-feature-text">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
          </article>
        ))}
      </div>

      <div className="teaser-cta">
        <Link
          to="/services"
          className="btn-teaser"
          onClick={() =>
            trackMatomoEvent('CTA', 'click', 'Services teaser button')
          }
        >
          {t('home.services_teaser_cta')}
        </Link>
      </div>
    </section>
  );
};

export default ServicesTeaser;
